import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/lib/auth-context';
import { dashboardAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, Clock, Search, Filter, X, Bell } from 'lucide-react';
import SortableHeader, { sortBy } from '@/components/SortableHeader';
import logger from '@/lib/logger';

const urgencyBorderClass = (daysLeft) => {
  if (daysLeft < 0) return 'border-l-red-500';
  if (daysLeft === 0) return 'border-l-orange-500';
  return 'border-l-amber-400';
};

const urgencyBadgeClass = (daysLeft) => {
  if (daysLeft < 0) return 'bg-red-100 text-red-700';
  if (daysLeft === 0) return 'bg-orange-100 text-orange-700';
  return 'bg-amber-100 text-amber-700';
};

const urgencyLabel = (daysLeft) => {
  if (daysLeft < 0) return `${Math.abs(daysLeft)}d overdue`;
  if (daysLeft === 0) return 'Due today';
  return `${daysLeft}d left`;
};

const urgencyRowClass = (daysLeft) => {
  if (daysLeft < 0) return 'bg-red-50/40';
  if (daysLeft === 0) return 'bg-orange-50/40';
  return '';
};

const urgencyLabelShort = (daysLeft) => {
  if (daysLeft < 0) return `${Math.abs(daysLeft)}d overdue`;
  if (daysLeft === 0) return 'Today';
  return `${daysLeft}d left`;
};

const STATUS_LABELS = {
  not_started: 'Not Started', documents_pending: 'Docs Pending',
  documents_received: 'Docs Received', further_docs_required: 'Further Docs Required',
  ready_for_review: 'Ready for Review', tax_payable: 'Tax Payable',
  ready_to_file: 'Ready to File', in_progress: 'In Progress',
};

const STATUS_COLORS = {
  not_started: 'bg-gray-100 text-gray-600', documents_pending: 'bg-amber-100 text-amber-700',
  documents_received: 'bg-blue-100 text-blue-700', further_docs_required: 'bg-orange-100 text-orange-700',
  ready_for_review: 'bg-purple-100 text-purple-700', tax_payable: 'bg-rose-100 text-rose-700',
  ready_to_file: 'bg-indigo-100 text-indigo-700', in_progress: 'bg-blue-100 text-blue-700',
};

const getPeriodLabel = (period) => {
  if (period.includes('H1')) return 'H1 (Apr-Sep)';
  if (period.includes('H2')) return 'H2 (Oct-Mar)';
  if (period.includes('Q')) { const q = period.split('-')[1]; const labels = { Q1: 'Q1 (Apr-Jun)', Q2: 'Q2 (Jul-Sep)', Q3: 'Q3 (Oct-Dec)', Q4: 'Q4 (Jan-Mar)' }; return labels[q] || q; }
  if (period.startsWith('FY')) return `FY ${period.replace('FY', '')}`;
  const [y, m] = period.split('-'); const names = { '01':'Jan','02':'Feb','03':'Mar','04':'Apr','05':'May','06':'Jun','07':'Jul','08':'Aug','09':'Sep','10':'Oct','11':'Nov','12':'Dec' };
  return `${names[m]}'${y.slice(2)}`;
};

const UpcomingDuePage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [boardFilter, setBoardFilter] = useState('all');
  const [subtaskFilter, setSubtaskFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [dueDateFilter, setDueDateFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [sort, setSort] = useState({ field: 'days_left', dir: 'asc' });

  useEffect(() => {
    dashboardAPI.upcomingDue()
      .then(res => setItems(res.data || []))
      .catch(err => logger.error('Failed to load upcoming due', err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const boards = useMemo(() => {
    const map = {};
    items.forEach(i => { if (i.board_name) map[i.board_name] = true; });
    return Object.keys(map).sort();
  }, [items]);

  const subtasks = useMemo(() => {
    const map = {};
    items.forEach(i => {
      if (i.subtask_title && (boardFilter === 'all' || i.board_name === boardFilter)) {
        map[i.subtask_title] = true;
      }
    });
    return Object.keys(map).sort();
  }, [items, boardFilter]);

  const statuses = useMemo(() => {
    const map = {};
    items.forEach(i => { if (i.status) map[i.status] = true; });
    return Object.keys(map).sort();
  }, [items]);

  const assignees = useMemo(() => {
    const map = {};
    items.forEach(i => { map[i.assigned_name || 'Unassigned'] = true; });
    return Object.keys(map).sort();
  }, [items]);

  // Periods narrow down to whatever board / compliance combo is currently
  // selected — so picking "GST + GSTR-1" only shows the periods actually
  // pending for that exact subtask, not every period across the firm.
  const periods = useMemo(() => {
    const map = {};
    items.forEach(i => {
      if (boardFilter !== 'all' && i.board_name !== boardFilter) return;
      if (subtaskFilter !== 'all' && i.subtask_title !== subtaskFilter) return;
      if (i.period) map[i.period] = true;
    });
    // Sort by chronological key — periods can be "2026-04", "FY2025-26", "2026-Q1"
    return Object.keys(map).sort();
  }, [items, boardFilter, subtaskFilter]);

  const dueDates = useMemo(() => {
    const map = {};
    items.forEach(i => {
      if (boardFilter !== 'all' && i.board_name !== boardFilter) return;
      if (subtaskFilter !== 'all' && i.subtask_title !== subtaskFilter) return;
      if (i.due_date) map[i.due_date] = true;
    });
    return Object.keys(map).sort();  // ISO dates sort naturally
  }, [items, boardFilter, subtaskFilter]);

  const clients = useMemo(() => {
    const map = {};
    items.forEach(i => { if (i.client_name) map[i.client_name] = true; });
    return Object.keys(map).sort();
  }, [items]);

  // Group by subtask+period for summary
  const grouped = useMemo(() => {
    const map = {};
    items.forEach(i => {
      const key = `${i.subtask_title}|${i.period}`;
      if (!map[key]) map[key] = { ...i, clients: [] };
      map[key].clients.push(i);
    });
    return Object.values(map).sort((a, b) => a.days_left - b.days_left);
  }, [items]);

  const filtered = useMemo(() => {
    const base = items.filter(i => {
      if (search) {
        const q = search.toLowerCase();
        if (![i.client_name, i.subtask_title, i.board_name, i.assigned_name].some(v => (v || '').toLowerCase().includes(q))) return false;
      }
      if (boardFilter !== 'all' && i.board_name !== boardFilter) return false;
      if (subtaskFilter !== 'all' && i.subtask_title !== subtaskFilter) return false;
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      if (assigneeFilter !== 'all' && (i.assigned_name || 'Unassigned') !== assigneeFilter) return false;
      if (periodFilter !== 'all' && i.period !== periodFilter) return false;
      if (dueDateFilter !== 'all' && i.due_date !== dueDateFilter) return false;
      if (clientFilter !== 'all' && i.client_name !== clientFilter) return false;
      if (urgencyFilter === 'overdue' && i.days_left >= 0) return false;
      if (urgencyFilter === 'due_today' && i.days_left !== 0) return false;
      if (urgencyFilter === 'due_soon' && (i.days_left < 1 || i.days_left > 7)) return false;
      return true;
    });

    // Sort
    if (sort.field && sort.dir) {
      const getters = {
        days_left: i => i.days_left,
        board_name: i => i.board_name,
        subtask_title: i => i.subtask_title,
        period: i => i.period,
        due_date: i => i.due_date,
        client_name: i => (i.client_name || '').toLowerCase(),
        status: i => i.status,
        assigned_name: i => (i.assigned_name || '').toLowerCase(),
      };
      return sortBy(base, getters[sort.field] || (i => i[sort.field]), sort.dir);
    }
    return base;
  }, [items, search, boardFilter, subtaskFilter, statusFilter, assigneeFilter, periodFilter, dueDateFilter, clientFilter, urgencyFilter, sort]);

  const hasActiveFilters = search || boardFilter !== 'all' || subtaskFilter !== 'all' ||
    statusFilter !== 'all' || assigneeFilter !== 'all' || periodFilter !== 'all' ||
    dueDateFilter !== 'all' || clientFilter !== 'all' || urgencyFilter !== 'all';

  const clearAll = () => {
    setSearch(''); setBoardFilter('all'); setSubtaskFilter('all');
    setStatusFilter('all'); setAssigneeFilter('all'); setUrgencyFilter('all');
    setPeriodFilter('all'); setDueDateFilter('all'); setClientFilter('all');
  };

  const overdueCount = items.filter(i => i.days_left < 0).length;
  const dueTodayCount = items.filter(i => i.days_left === 0).length;
  const dueSoonCount = items.filter(i => i.days_left > 0 && i.days_left <= 7).length;

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="upcoming-due-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-semibold">Compliance Radar</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{items.length} pending returns across {boards.length} boards</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {overdueCount > 0 && (
            <Badge className="bg-red-100 text-red-700 cursor-pointer text-xs" onClick={() => setUrgencyFilter(urgencyFilter === 'overdue' ? 'all' : 'overdue')} data-testid="badge-overdue">
              <AlertTriangle className="h-3 w-3 mr-1" /> Overdue: {overdueCount}
            </Badge>
          )}
          {dueTodayCount > 0 && (
            <Badge className="bg-orange-100 text-orange-700 cursor-pointer text-xs" onClick={() => setUrgencyFilter(urgencyFilter === 'due_today' ? 'all' : 'due_today')} data-testid="badge-due-today">
              Due Today: {dueTodayCount}
            </Badge>
          )}
          {dueSoonCount > 0 && (
            <Badge className="bg-amber-100 text-amber-700 cursor-pointer text-xs" onClick={() => setUrgencyFilter(urgencyFilter === 'due_soon' ? 'all' : 'due_soon')} data-testid="badge-due-soon">
              <Clock className="h-3 w-3 mr-1" /> Due Soon (1-7 days): {dueSoonCount}
            </Badge>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {grouped.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {grouped.slice(0, 8).map(g => {
            return (
              <Card key={`${g.subtask_title}-${g.period}`} className={`border-l-4 ${urgencyBorderClass(g.days_left)}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold">{g.subtask_title}</span>
                    <Badge variant="outline" className="text-[10px]">{g.board_name}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{getPeriodLabel(g.period)} — Due: {g.due_date}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs">{g.clients.length} client(s) pending</span>
                    <Badge className={`text-[10px] ${urgencyBadgeClass(g.days_left)}`}>
                      {urgencyLabel(g.days_left)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters — primary row */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search client, compliance, board, team..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="upcoming-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded hover:bg-muted flex items-center justify-center text-muted-foreground"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="upcoming-urgency-filter">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({items.length})</SelectItem>
              <SelectItem value="overdue">Overdue ({overdueCount})</SelectItem>
              <SelectItem value="due_today">Due Today ({dueTodayCount})</SelectItem>
              <SelectItem value="due_soon">Due Soon 1-7d ({dueSoonCount})</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" className="h-9 text-xs gap-1" onClick={clearAll} data-testid="upcoming-clear-filters">
              <X className="h-3.5 w-3.5" /> Clear all
            </Button>
          )}
          <span className="text-sm text-muted-foreground ml-auto" data-testid="upcoming-result-count">{filtered.length} of {items.length}</span>
        </div>

        {/* Filters — secondary row (board / compliance / status / assignee) */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1">
            <Filter className="h-3 w-3" /> Filter by
          </span>
          <Select value={boardFilter} onValueChange={v => { setBoardFilter(v); setSubtaskFilter('all'); }}>
            <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="upcoming-board-filter">
              <SelectValue placeholder="Board" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Boards</SelectItem>
              {boards.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={subtaskFilter} onValueChange={setSubtaskFilter}>
            <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="upcoming-subtask-filter">
              <SelectValue placeholder="Compliance / Subtask" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Compliances</SelectItem>
              {subtasks.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="upcoming-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {statuses.map(s => (
                <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="upcoming-assignee-filter">
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              {assignees.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="upcoming-period-filter">
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Periods</SelectItem>
              {periods.map(p => <SelectItem key={p} value={p}>{getPeriodLabel(p)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={dueDateFilter} onValueChange={setDueDateFilter}>
            <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="upcoming-due-date-filter">
              <SelectValue placeholder="Due Date" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Due Dates</SelectItem>
              {dueDates.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="upcoming-client-filter">
              <SelectValue placeholder="Client" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Active filter chips — one-click removal */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-1.5">
            {search && (
              <Badge variant="secondary" className="gap-1 h-6 text-[11px]">
                Search: "{search}"
                <button type="button" onClick={() => setSearch('')} className="ml-1 hover:text-foreground"><X className="h-3 w-3" /></button>
              </Badge>
            )}
            {boardFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1 h-6 text-[11px]">
                Board: {boardFilter}
                <button type="button" onClick={() => setBoardFilter('all')} className="ml-1"><X className="h-3 w-3" /></button>
              </Badge>
            )}
            {subtaskFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1 h-6 text-[11px]">
                Compliance: {subtaskFilter}
                <button type="button" onClick={() => setSubtaskFilter('all')} className="ml-1"><X className="h-3 w-3" /></button>
              </Badge>
            )}
            {statusFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1 h-6 text-[11px]">
                Status: {STATUS_LABELS[statusFilter] || statusFilter}
                <button type="button" onClick={() => setStatusFilter('all')} className="ml-1"><X className="h-3 w-3" /></button>
              </Badge>
            )}
            {assigneeFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1 h-6 text-[11px]">
                Assignee: {assigneeFilter}
                <button type="button" onClick={() => setAssigneeFilter('all')} className="ml-1"><X className="h-3 w-3" /></button>
              </Badge>
            )}
            {periodFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1 h-6 text-[11px]">
                Period: {getPeriodLabel(periodFilter)}
                <button type="button" onClick={() => setPeriodFilter('all')} className="ml-1"><X className="h-3 w-3" /></button>
              </Badge>
            )}
            {dueDateFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1 h-6 text-[11px]">
                Due Date: {dueDateFilter}
                <button type="button" onClick={() => setDueDateFilter('all')} className="ml-1"><X className="h-3 w-3" /></button>
              </Badge>
            )}
            {clientFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1 h-6 text-[11px]">
                Client: {clientFilter}
                <button type="button" onClick={() => setClientFilter('all')} className="ml-1"><X className="h-3 w-3" /></button>
              </Badge>
            )}
            {urgencyFilter !== 'all' && (
              <Badge variant="secondary" className="gap-1 h-6 text-[11px]">
                Urgency: {urgencyFilter.replace('_', ' ')}
                <button type="button" onClick={() => setUrgencyFilter('all')} className="ml-1"><X className="h-3 w-3" /></button>
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">{items.length === 0 ? 'No upcoming due returns. All clear!' : 'No returns match your filters.'}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead><SortableHeader field="days_left" sort={sort} onChange={setSort}>Urgency</SortableHeader></TableHead>
                <TableHead><SortableHeader field="board_name" sort={sort} onChange={setSort}>Board</SortableHeader></TableHead>
                <TableHead><SortableHeader field="subtask_title" sort={sort} onChange={setSort}>Compliance</SortableHeader></TableHead>
                <TableHead><SortableHeader field="period" sort={sort} onChange={setSort}>Period</SortableHeader></TableHead>
                <TableHead><SortableHeader field="due_date" sort={sort} onChange={setSort}>Due Date</SortableHeader></TableHead>
                <TableHead><SortableHeader field="client_name" sort={sort} onChange={setSort}>Client</SortableHeader></TableHead>
                <TableHead><SortableHeader field="status" sort={sort} onChange={setSort}>Status</SortableHeader></TableHead>
                <TableHead><SortableHeader field="assigned_name" sort={sort} onChange={setSort}>Assigned To</SortableHeader></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item, idx) => {
                return (
                  <TableRow key={`${item.subtask_title}-${item.period}-${item.client_id}-${idx}`} className={urgencyRowClass(item.days_left)} data-testid="upcoming-due-row">
                    <TableCell>
                      <Badge className={`text-[10px] ${urgencyBadgeClass(item.days_left)}`}>
                        {urgencyLabelShort(item.days_left)}
                      </Badge>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{item.board_name}</Badge></TableCell>
                    <TableCell className="text-sm font-medium">{item.subtask_title}</TableCell>
                    <TableCell className="text-sm">{getPeriodLabel(item.period)}</TableCell>
                    <TableCell className="text-sm">{item.due_date}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-medium text-primary">{item.client_name?.[0]?.toUpperCase()}</div>
                        <span className="text-sm">{item.client_name}</span>
                      </div>
                    </TableCell>
                    <TableCell><Badge className={`text-[10px] ${STATUS_COLORS[item.status] || 'bg-gray-100'}`}>{STATUS_LABELS[item.status] || item.status}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{item.assigned_name || '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default UpcomingDuePage;
